import { useState, useRef, useEffect, useCallback } from 'react'
import { VoiceButton, VoiceButtonState } from './VoiceButton'
import { TranscriptDisplay, TranscriptMessage } from './TranscriptDisplay'
import { FloatingAudioControl } from '../shared/FloatingAudioControl'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog'
import { ScrollArea } from '../ui/scroll-area'
import { Plus, MessageSquare } from 'lucide-react'
import { toast, Toaster } from 'sonner'
import { API_BASE_URL } from '../../lib/config'

interface ChatSession {
  id: string
  name: string
  created_at?: string
}

export function VoiceAgent() {
  // Speech recognition
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const [isListening, setIsListening] = useState(false)
  const [interimTranscript, setInterimTranscript] = useState('')
  
  // Voice button state
  const [voiceButtonState, setVoiceButtonState] = useState<VoiceButtonState>('idle')
  
  // Audio playback
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const audioBlobUrlRef = useRef<string | null>(null)
  const playbackQueueRef = useRef<Uint8Array[]>([])
  const isProcessingPlaybackRef = useRef(false)
  
  // Transcript
  const [messages, setMessages] = useState<TranscriptMessage[]>([])
  
  // Chat sessions
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([])
  const [currentChatId, setCurrentChatId] = useState<string | null>(null)
  const [currentChatName, setCurrentChatName] = useState<string>('Voice Chat')
  const [isChatListOpen, setIsChatListOpen] = useState(false)
  const [, setIsLoadingSessions] = useState(false)
  
  // API state
  const abortControllerRef = useRef<AbortController | null>(null)
  const accumulatedTextRef = useRef('')
  const accumulatedAudioRef = useRef<Uint8Array[]>([])

  // Initialize speech recognition
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition is not supported in your browser')
      return
    }

    const SpeechRecognition = (window as unknown as { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).SpeechRecognition || 
      (window as unknown as { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onstart = () => {
      setIsListening(true)
      setVoiceButtonState('listening')
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = ''
      let final = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          final += transcript + ' '
        } else {
          interim += transcript
        }
      }

      if (final) {
        handleUserMessage(final.trim())
        setInterimTranscript('')
      } else {
        setInterimTranscript(interim)
      }
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error)
      if (event.error === 'no-speech') {
        // User stopped speaking, don't show error
        return
      }
      toast.error(`Speech recognition error: ${event.error}`)
      stopListening()
    }

    recognition.onend = () => {
      setIsListening(false)
      setVoiceButtonState((prev) => prev === 'listening' ? 'idle' : prev)
    }

    recognitionRef.current = recognition

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load chat sessions on mount
  useEffect(() => {
    loadChatSessions()
  }, [])

  // Auto-load most recent voice chat
  useEffect(() => {
    if (chatSessions.length > 0 && !currentChatId) {
      const voiceChats = chatSessions.filter(s => s.name && s.name.includes('Voice Chat'))
      if (voiceChats.length > 0) {
        const mostRecent = voiceChats[0]
        if (mostRecent.id && mostRecent.id !== 'undefined') {
          const chatId = String(mostRecent.id)
          setCurrentChatId(chatId)
          setCurrentChatName(mostRecent.name)
          loadChatHistory(chatId)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessions])

  // Stop listening when AI is speaking
  useEffect(() => {
    if (isPlaying && isListening) {
      stopListening()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isListening])

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioBlobUrlRef.current) {
        URL.revokeObjectURL(audioBlobUrlRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const loadChatSessions = async () => {
    setIsLoadingSessions(true)
    try {
      const pageSize = 50
      const sessions: Array<{ id: string | number; name?: string; session_name?: string; created_at?: string }> = []
      let offset = 0
      let hasMore = true
      while (hasMore) {
        const res = await fetch(`${API_BASE_URL}chat/sessions?limit=${pageSize}&offset=${offset}`, {
          credentials: 'include'
        })
        if (!res.ok) throw new Error('Failed to load sessions')
        const envelope = await res.json() as {
          sessions?: Array<{ id: string | number; name?: string; session_name?: string; created_at?: string }>
          has_more?: boolean
        }
        const page = Array.isArray(envelope) ? envelope : (envelope.sessions ?? [])
        sessions.push(...page)
        hasMore = !Array.isArray(envelope) && envelope.has_more === true
        offset += page.length
        if (page.length === 0) break
      }
      // Filter for voice chats - check both 'name' and 'session_name' fields
      const voiceChats = sessions
        .filter(s => {
          const name = s.name || s.session_name || ''
          return name.includes('Voice Chat')
        })
        .map(s => ({
          id: String(s.id),
          name: s.name || s.session_name || 'Voice Chat',
          created_at: s.created_at
        }))
      setChatSessions(voiceChats)
    } catch (error) {
      console.error('Error loading chat sessions:', error)
      toast.error('Failed to load chat sessions')
    } finally {
      setIsLoadingSessions(false)
    }
  }

  const loadChatHistory = async (chatId: string) => {
    if (!chatId || chatId === 'undefined') {
      console.warn('loadChatHistory called with invalid chatId:', chatId)
      return
    }
    try {
      const res = await fetch(`${API_BASE_URL}chat/stream-history/${chatId}`, {
        credentials: 'include'
      })
      if (!res.ok) {
        if (res.status === 404) {
          // Chat doesn't exist yet, start fresh
          setMessages([])
          return
        }
        throw new Error('Failed to load chat history')
      }
      const data = await res.json()
      
      const historyMessages: TranscriptMessage[] = (data.history || []).map((msg: { id: string; role: string; content: string; timestamp?: string }) => ({
        id: msg.id,
        content: msg.content,
        sender: msg.role === 'user' ? 'user' : 'ai',
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
      }))
      
      setMessages(historyMessages)
    } catch (error) {
      console.error('Error loading chat history:', error)
      // Don't show error toast for 404s, just start fresh
      if (error instanceof Error && !error.message.includes('404')) {
        toast.error('Failed to load chat history')
      }
      setMessages([])
    }
  }

  const createNewChat = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}create-initial-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: 'Voice Chat' })
      })
      if (!res.ok) {
        const errorText = await res.text()
        console.error('Failed to create chat:', res.status, errorText)
        throw new Error(`Failed to create chat: ${res.status}`)
      }
      const data = await res.json() as { id?: string; chat_id?: string; name?: string }
      const chatId = data.id || data.chat_id
      if (!chatId) {
        console.error('Response data:', data)
        throw new Error('No chat ID in response')
      }
      const newChat: ChatSession = {
        id: String(chatId),
        name: 'Voice Chat',
        created_at: new Date().toISOString()
      }
      setChatSessions(prev => [newChat, ...prev])
      setCurrentChatId(newChat.id)
      setCurrentChatName(newChat.name)
      setMessages([])
      setIsChatListOpen(false)
      return newChat.id
    } catch (error) {
      console.error('Error creating new chat:', error)
      toast.error('Failed to create new chat')
      throw error
    }
  }

  const handleUserMessage = async (text: string) => {
    if (!text.trim()) return

    // Add user message to transcript
    const userMessage: TranscriptMessage = {
      id: Date.now().toString(),
      content: text,
      sender: 'user',
      timestamp: new Date()
    }
    setMessages(prev => [...prev, userMessage])

    // Stop listening
    stopListening()
    setVoiceButtonState('processing')

    // Get or create chat ID
    let chatIdToUse = currentChatId
    if (!chatIdToUse) {
      try {
        const newChatId = await createNewChat()
        chatIdToUse = newChatId || currentChatId
      } catch (error) {
        console.error('Error creating chat:', error)
        toast.error('Failed to create chat session')
        setVoiceButtonState('idle')
        return
      }
    }

    if (!chatIdToUse) {
      toast.error('Failed to get chat session')
      setVoiceButtonState('idle')
      return
    }

    // Prepare history
    const history = messages.map(m => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      content: m.content
    }))

    // Call voice-stream endpoint
    await streamVoiceResponse(text, history, chatIdToUse)
  }

  const streamVoiceResponse = async (message: string, history: Array<{ role: string; content: string }>, chatId: string) => {
    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    // Reset accumulators
    accumulatedTextRef.current = ''
    accumulatedAudioRef.current = []
    playbackQueueRef.current = []

    try {
      const formData = new FormData()
      formData.append('message', message)
      formData.append('history', JSON.stringify(history))
      formData.append('chat_id', chatId)
      formData.append('session_name', 'Voice Chat')

      const res = await fetch(`${API_BASE_URL}voice-stream`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        signal: abortControllerRef.current.signal
      })

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`)
      }

      if (!res.body) {
        throw new Error('No response body')
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''

      try {
        while (true) {
          const { value, done } = await reader.read()
          
          if (done) {
            // Process any remaining buffer
            if (buffer.trim()) {
              const lines = buffer.split('\n')
              for (const line of lines) {
                if (!line.trim()) continue
                processLine(line)
              }
            }
            break
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true })
          
          // Process complete lines
          const lines = buffer.split('\n')
          // Keep the last incomplete line in buffer
          buffer = lines.pop() || ''
          
          for (const line of lines) {
            if (!line.trim()) continue
            processLine(line)
          }
        }
      } catch (streamError) {
        console.error('Stream reading error:', streamError)
        // Process any remaining buffer even on error
        if (buffer.trim()) {
          const lines = buffer.split('\n')
          for (const line of lines) {
            if (!line.trim()) continue
            processLine(line)
          }
        }
        throw streamError
      }

      function processLine(line: string) {
        try {
          const data = JSON.parse(line)
          
          if (data.type === 'text') {
            // Accumulate text
            accumulatedTextRef.current += data.content
            
            // Update transcript with streaming text
            setMessages(prev => {
              const withoutStreaming = prev.filter(m => m.id !== 'streaming')
              return [...withoutStreaming, {
                id: 'streaming',
                content: accumulatedTextRef.current,
                sender: 'ai' as const,
                timestamp: new Date()
              }]
            })
          } else if (data.type === 'audio' && data.chunk) {
            // Decode base64 audio chunk
            try {
              const audioBytes = Uint8Array.from(atob(data.chunk), c => c.charCodeAt(0))
              accumulatedAudioRef.current.push(audioBytes)
              
              // Start playback when we have 8KB or stream is complete
              const totalAudioSize = accumulatedAudioRef.current.reduce((sum, chunk) => sum + chunk.length, 0)
              if (totalAudioSize >= 8192) {
                // Move accumulated to queue and start playback
                playbackQueueRef.current.push(...accumulatedAudioRef.current)
                accumulatedAudioRef.current = []
                if (!isPaused && !isProcessingPlaybackRef.current) {
                  processAudioPlayback()
                }
              }
            } catch (audioError) {
              console.error('Error decoding audio chunk:', audioError)
            }
          }
        } catch {
          // Not JSON, might be plain text chunk or empty line
          if (line.trim()) {
            accumulatedTextRef.current += line
            setMessages(prev => {
              const withoutStreaming = prev.filter(m => m.id !== 'streaming')
              return [...withoutStreaming, {
                id: 'streaming',
                content: accumulatedTextRef.current,
                sender: 'ai' as const,
                timestamp: new Date()
              }]
            })
          }
        }
      }

      // Finalize message
      setMessages(prev => {
        const withoutStreaming = prev.filter(m => m.id !== 'streaming')
        const finalMessage: TranscriptMessage = {
          id: Date.now().toString(),
          content: accumulatedTextRef.current,
          sender: 'ai',
          timestamp: new Date()
        }
        return [...withoutStreaming, finalMessage]
      })

      // Process any remaining audio
      if (accumulatedAudioRef.current.length > 0) {
        playbackQueueRef.current.push(...accumulatedAudioRef.current)
        accumulatedAudioRef.current = []
        if (!isPaused && !isProcessingPlaybackRef.current) {
          processAudioPlayback()
        }
      }

      setVoiceButtonState('speaking')
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }
      console.error('Error streaming voice response:', error)
      toast.error('Failed to get voice response')
      setVoiceButtonState('idle')
    }
  }

  const processAudioPlayback = useCallback(async () => {
    if (isProcessingPlaybackRef.current) return
    if (playbackQueueRef.current.length === 0) {
      // Check if we have accumulated audio to play
      if (accumulatedAudioRef.current.length > 0) {
        const chunks = [...accumulatedAudioRef.current]
        accumulatedAudioRef.current = []
        playbackQueueRef.current = chunks
      } else {
        return
      }
    }
    // Check pause state from ref to avoid stale closure
    if (isPaused) return

    isProcessingPlaybackRef.current = true

    while (playbackQueueRef.current.length > 0 && !isPaused) {
      // Get all chunks in queue
      const chunks = playbackQueueRef.current.splice(0)
      if (chunks.length === 0) break

      // Merge chunks
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
      const merged = new Uint8Array(totalLength)
      let offset = 0
      for (const chunk of chunks) {
        merged.set(chunk, offset)
        offset += chunk.length
      }

      // Create blob and play
      const blob = new Blob([merged], { type: 'audio/mpeg' })
      const url = URL.createObjectURL(blob)

      await new Promise<void>((resolve) => {
        if (!audioRef.current) {
          audioRef.current = new Audio()
        }

        const audio = audioRef.current
        
        // Clean up previous URL if exists
        if (audioBlobUrlRef.current) {
          URL.revokeObjectURL(audioBlobUrlRef.current)
        }
        audioBlobUrlRef.current = url
        
        audio.src = url
        audio.onended = () => {
          URL.revokeObjectURL(url)
          audioBlobUrlRef.current = null
          resolve()
        }
        audio.onerror = () => {
          URL.revokeObjectURL(url)
          audioBlobUrlRef.current = null
          resolve()
        }

        setIsPlaying(true)
        setVoiceButtonState('speaking')
        audio.play().catch((err) => {
          console.error('Audio play error:', err)
          toast.error('Audio playback blocked. Please interact with the page first.')
          URL.revokeObjectURL(url)
          audioBlobUrlRef.current = null
          resolve()
        })
      })

      // Check if more chunks arrived while playing
      if (accumulatedAudioRef.current.length > 0) {
        playbackQueueRef.current.push(...accumulatedAudioRef.current)
        accumulatedAudioRef.current = []
      }
    }

    isProcessingPlaybackRef.current = false
    setIsPlaying(false)
    setVoiceButtonState('idle')
  }, [isPaused])

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start()
    }
  }

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop()
      setIsListening(false)
      setInterimTranscript('')
    }
  }

  const handleMicClick = () => {
    if (isListening) {
      stopListening()
    } else {
      startListening()
    }
  }

  const handlePlay = () => {
    setIsPaused(false)
    // Small delay to ensure state is updated
    setTimeout(() => {
      if (playbackQueueRef.current.length > 0 || accumulatedAudioRef.current.length > 0) {
        if (accumulatedAudioRef.current.length > 0) {
          playbackQueueRef.current.push(...accumulatedAudioRef.current)
          accumulatedAudioRef.current = []
        }
        processAudioPlayback()
      }
    }, 10)
  }

  const handlePause = () => {
    setIsPaused(true)
    if (audioRef.current) {
      audioRef.current.pause()
    }
  }

  const handleStop = () => {
    setIsPaused(false)
    setIsPlaying(false)
    playbackQueueRef.current = []
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    setVoiceButtonState('idle')
  }

  const handleChatSelect = (chatId: string) => {
    if (!chatId || chatId === 'undefined') {
      console.warn('handleChatSelect called with invalid chatId:', chatId)
      return
    }
    setCurrentChatId(chatId)
    const selectedChat = chatSessions.find(s => s.id === chatId)
    if (selectedChat) {
      setCurrentChatName(selectedChat.name)
      loadChatHistory(chatId)
    } else {
      // If chat not found in list, try to load it anyway
      loadChatHistory(chatId)
    }
    setIsChatListOpen(false)
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background/50 backdrop-blur-sm px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold">Voice Agent</h1>
        <div className="flex items-center gap-2">
          <Dialog open={isChatListOpen} onOpenChange={setIsChatListOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <MessageSquare className="w-4 h-4 mr-2" />
                Chats
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Voice Chats</DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={createNewChat}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Voice Chat
                  </Button>
                  {chatSessions.map((session) => (
                    <Button
                      key={session.id}
                      variant={currentChatId === session.id ? 'default' : 'ghost'}
                      className="w-full justify-start"
                      onClick={() => handleChatSelect(session.id)}
                    >
                      {session.name}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Transcript */}
      <div className="flex-1 overflow-hidden">
        <TranscriptDisplay
          messages={messages}
          currentChatName={currentChatName}
        />
      </div>

      {/* Voice Button */}
      <div className="border-t bg-background/50 backdrop-blur-sm px-4 py-6 flex items-center justify-center">
        <VoiceButton
          state={voiceButtonState}
          onClick={handleMicClick}
          disabled={isPlaying && !isPaused}
        />
        {interimTranscript && (
          <div className="ml-4 text-sm text-muted-foreground">
            {interimTranscript}
          </div>
        )}
      </div>

      {/* Floating Audio Control */}
      <FloatingAudioControl
        isPlaying={isPlaying}
        isPaused={isPaused}
        onPlay={handlePlay}
        onPause={handlePause}
        onStop={handleStop}
        position="bottom-right"
      />

      {/* Toast Notifications */}
      <Toaster 
        theme="dark" 
        position="top-right"
        toastOptions={{
          style: {
            background: 'rgb(15 23 42)',
            color: '#fff',
          },
        }}
      />
    </div>
  )
}

